import { API_PATH } from './config';
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    CircularProgress,
    Alert,
    Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

// Dark admin theme tokens (shared look with AdminConsole)
const dk = {
    bg: '#0d1117',
    card: '#161b22',
    elevated: '#21262d',
    hover: '#2d333b',
    border: 'rgba(99, 102, 241, 0.2)',
    borderHover: 'rgba(99, 102, 241, 0.4)',
    textPrimary: '#e6edf3',
    textSecondary: '#8b949e',
    textMuted: '#6e7681',
    accent: '#6366f1',
    accentLight: '#818cf8',
};

const palette = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#ec4899', '#84cc16'];

// A compact metric tile
const Stat = ({ value, label, color = dk.accentLight, hint }) => (
    <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none', height: '100%' }}>
        <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
            <Typography variant="h4" sx={{ color, fontWeight: 700 }}>{value}</Typography>
            <Typography variant="body2" sx={{ color: dk.textSecondary, mt: 0.5 }}>{label}</Typography>
            {hint && <Typography variant="caption" sx={{ color: dk.textMuted, display: 'block' }}>{hint}</Typography>}
        </CardContent>
    </Card>
);

// A daily bar chart from a [{date, count}] series
const BarChartDaily = ({ title, series, color = dk.accent }) => {
    const max = Math.max(1, ...series.map(d => d.count));
    return (
        <Paper sx={{ p: 3, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none', height: '100%' }}>
            <Typography variant="h6" sx={{ color: dk.textPrimary, fontWeight: 700, mb: 2 }}>{title}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 140 }}>
                {series.map((d) => (
                    <Tooltip key={d.date} title={`${d.date}: ${d.count}`} arrow>
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 3,
                                height: `${(d.count / max) * 100}%`,
                                minHeight: d.count > 0 ? 2 : 0,
                                bgcolor: color,
                                borderRadius: '2px 2px 0 0',
                                opacity: d.count > 0 ? 0.9 : 0.15,
                                transition: 'opacity 0.15s',
                                '&:hover': { opacity: 1 },
                            }}
                        />
                    </Tooltip>
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" sx={{ color: dk.textMuted }}>{series[0]?.date}</Typography>
                <Typography variant="caption" sx={{ color: dk.textMuted }}>{series[series.length - 1]?.date}</Typography>
            </Box>
        </Paper>
    );
};

// A horizontal breakdown list from [{label, count}]
const Breakdown = ({ title, items }) => {
    const total = Math.max(1, items.reduce((s, i) => s + i.count, 0));
    return (
        <Paper sx={{ p: 3, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none', height: '100%' }}>
            <Typography variant="h6" sx={{ color: dk.textPrimary, fontWeight: 700, mb: 2 }}>{title}</Typography>
            {items.length === 0 && (
                <Typography variant="body2" sx={{ color: dk.textMuted }}>No data.</Typography>
            )}
            {items.map((item, idx) => (
                <Box key={item.label} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: dk.textPrimary, textTransform: 'capitalize' }}>{item.label}</Typography>
                        <Typography variant="body2" sx={{ color: dk.textSecondary }}>
                            {item.count} <Box component="span" sx={{ color: dk.textMuted }}>({Math.round((item.count / total) * 100)}%)</Box>
                        </Typography>
                    </Box>
                    <Box sx={{ height: 8, bgcolor: dk.elevated, borderRadius: 4, overflow: 'hidden' }}>
                        <Box sx={{ width: `${(item.count / total) * 100}%`, height: '100%', bgcolor: palette[idx % palette.length], borderRadius: 4 }} />
                    </Box>
                </Box>
            ))}
        </Paper>
    );
};

const SectionTitle = ({ children }) => (
    <Typography variant="overline" sx={{ color: dk.accentLight, fontWeight: 700, letterSpacing: '0.08em', display: 'block', mt: 1, mb: 1.5 }}>
        {children}
    </Typography>
);

const AdminStatistics = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const fetchStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_PATH}/admin/statistics.php`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.status === 403) { setError('Access denied. Admins only.'); return; }
            if (res.status === 401) { setError('Please log in to view statistics.'); return; }
            const json = await res.json();
            if (json.success) setData(json);
            else setError(json.error || 'Failed to load statistics.');
        } catch (e) {
            setError('Failed to connect to server: ' + e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchStats(); }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ flexGrow: 1, color: dk.textPrimary, fontWeight: 700 }}>
                    📈 Site Statistics
                </Typography>
                {data && (
                    <Typography variant="caption" sx={{ color: dk.textMuted, mr: 2 }}>
                        Generated: {data.generated_at}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <RefreshIcon />}
                    onClick={fetchStats}
                    disabled={loading}
                    sx={{ bgcolor: dk.accent, '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: dk.elevated, color: dk.textMuted } }}
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', '& .MuiAlert-icon': { color: '#f87171' } }}>
                    {error}
                </Alert>
            )}

            {loading && !data && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                    <CircularProgress sx={{ color: dk.accent }} />
                </Box>
            )}

            {data && (
                <>
                    {/* ===================== USERS ===================== */}
                    <SectionTitle>👥 Users</SectionTitle>
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.users.total_accounts} label="Total Accounts" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.users.registered_users} label="Registered Users" color="#10b981" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.users.total_guests} label="Guests" color="#f59e0b" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.users.verified_emails} label="Verified Emails" color="#06b6d4" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.users.new_today} label="New Today" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.users.new_7d} label="New (7 days)" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.users.new_30d} label="New (30 days)" /></Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                            <BarChartDaily title="Signups — last 30 days" series={data.users.signups_daily} color="#10b981" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Breakdown title="By Login Method" items={data.users.by_login_method} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Breakdown title="By Language" items={data.users.by_language} />
                        </Grid>
                    </Grid>

                    {/* ===================== TRAFFIC ===================== */}
                    <SectionTitle>🌐 Traffic</SectionTitle>
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.traffic.active_now} label="Active Now" color="#10b981" hint="last 15 min" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.traffic.active_users_24h} label="Active Users" hint="last 24h" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.traffic.active_users_7d} label="Active Users" hint="last 7 days" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.traffic.active_users_30d} label="Active Users" hint="last 30 days" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.traffic.total_sessions} label="Total Sessions" color={dk.textSecondary} /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.traffic.sessions_today} label="Sessions Today" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.traffic.sessions_7d} label="Sessions (7 days)" /></Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                            <BarChartDaily title="Sessions — last 30 days" series={data.traffic.sessions_daily} color="#6366f1" />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <BarChartDaily title="Daily Active Users — last 30 days" series={data.traffic.active_users_daily} color="#06b6d4" />
                        </Grid>
                    </Grid>

                    {/* ===================== CONTENT ===================== */}
                    <SectionTitle>🧳 Content</SectionTitle>
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.content.total_trips} label="Total Trips" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.content.trips_7d} label="Trips (7 days)" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.content.trips_30d} label="Trips (30 days)" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.content.total_activities} label="Total Activities" color="#10b981" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.content.ai_suggestions} label="AI Suggestions" color="#a855f7" /></Grid>
                        <Grid item xs={6} sm={4} md={3}><Stat value={data.content.avg_activities_per_trip} label="Avg Activities / Trip" color="#f59e0b" /></Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                            <BarChartDaily title="Trips Created — last 30 days" series={data.content.trips_daily} color="#f59e0b" />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Breakdown title="Top Departure Locations" items={data.content.top_destinations} />
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
};

export default AdminStatistics;
